import { useMemo } from "react";
import { useOrg } from "../org/useOrg";
import type { OrgRole } from "../org/types";
import {
  type Capability,
  type Role,
} from "./capabilities";
import { mergeCapabilities } from "./effective";

export interface CapabilityApi {
  can: (cap: Capability) => boolean;
  role: Role | null;
  allCapabilities: ReadonlySet<Capability>;
}

// OrgRole and Role currently coincide; re-declared here so the capability
// module can evolve independently of the org model.
function toRole(role: OrgRole | null): Role | null {
  return role;
}

const EMPTY_CAPS: ReadonlySet<Capability> = new Set<Capability>();

export function useCapabilities(): CapabilityApi {
  const { activeRole, activeCapabilityOverrides } = useOrg();
  const role = toRole(activeRole);

  return useMemo<CapabilityApi>(() => {
    if (!role) {
      return {
        can: () => false,
        role: null,
        allCapabilities: EMPTY_CAPS,
      };
    }
    // Wave 19: role defaults ⊕ per-member overrides. Owners ignore
    // overrides (mergeCapabilities short-circuits) so they always carry
    // the full default set.
    const caps = mergeCapabilities(role, activeCapabilityOverrides);
    return {
      can: (cap) => caps.has(cap),
      role,
      allCapabilities: caps,
    };
  }, [role, activeCapabilityOverrides]);
}
